import { Button, Dropdown, Icons, Search, SnackAlert, SnackAlertContent } from '@silverfort/sf-components';
import { FC, SVGProps, useCallback, useEffect, useState } from 'react';
import CreateKeyPopup from './CreateKeyPopup';
import DeleteAppUsersPopup from './DeleteAppUsersPopup';
import { APP_USER_PERMISSIONS } from '@silverfort-nodelibs/common/utils/consts';
import { httpDelete, httpGet, httpPost } from '../../../utils';
import { ExternalApi } from './ExternalApi';
import { AppUserProps, Filter } from './Types';
import { AuthUsersTable } from './AuthUsersTable';
import { SILVERFORT_API_FIELDS } from '../../common/fields';

const { APP_USER_INACTIVITY_THRESHOLD } = SILVERFORT_API_FIELDS;

const PERMISSIONS = Object.values(APP_USER_PERMISSIONS).map(({ value, label }) => ({ value, title: label }));

const FILTERS: Filter[] = [
    {
        text: 'Last used',
        columnKey: 'lastUsed',
        type: 'days',
        variant: 'filter',
        icon: Icons.Calendar as FC<SVGProps<SVGSVGElement>>,
        items: [
            { value: 1, title: 'Today' },
            { value: 2, title: 'Yesterday' },
            { value: 7, title: 'Last week' },
            { value: 30, title: 'Last month' },
            { value: 60, title: 'Last two months' }
        ],
        selectedItems: 30
    },
    {
        text: 'Status',
        columnKey: 'status',
        type: 'text',
        variant: 'filter',
        multiple: true,
        items: [
            { value: 'active', title: 'Active' },
            { value: 'expired', title: 'Expired' }
        ],
        selectedItems: ['active']
    },
    {
        text: 'Permissions',
        columnKey: 'permissions',
        type: 'text',
        variant: 'filter',
        multiple: true,
        items: PERMISSIONS,
        selectedItems: []
    },
    {
        text: 'Created by',
        columnKey: 'createdBy',
        type: 'text',
        variant: 'filter',
        multiple: true,
        items: [],
        selectedItems: [],
        searchable: true
    }
];

const AuthTokensAndUsers = ({ fields, dispatch, save }) => {
    const inactivityThreshold = fields[APP_USER_INACTIVITY_THRESHOLD.ID]?.value;

    const [appUsers, setAppUsers] = useState<AppUserProps[]>([]);

    const [showCreateKeyPopup, setShowCreateKeyPopup] = useState(false);
    const [usersToDelete, setUsersToDelete] = useState<string[]>([]);
    const [tempAppUserData, setTempAppUserData] = useState();
    const [snackAlertState, setSnackAlertState] = useState<{
        message: string;
        icon: FC<SVGProps<SVGSVGElement>>;
    }>();

    const [filters, setFilters] = useState(FILTERS);
    const [searchValue, setSearchValue] = useState('');

    useEffect(() => {
        getAppUsers();
    }, []);

    useEffect(() => {
        const creators = [
            ...appUsers.reduce<Set<string>>((acc, appUser) => {
                appUser.created_by && acc.add(appUser.created_by);
                return acc;
            }, new Set())
        ];
        setFilters((currentFilters) =>
            currentFilters.map((filter) =>
                filter.text === 'Created by'
                    ? { ...filter, items: creators.map((creator) => ({ value: creator, title: creator })) }
                    : filter
            )
        );
    }, [appUsers]);

    const getAppUsers = async () => {
        try {
            const { data } = await httpGet('/appUsers/getAllAppUsers');
            setAppUsers(data as unknown as AppUserProps[]);
        } catch (err) {
            console.error('Failed to get app users', err);
        }
    };

    const deleteAppUsers = async () => {
        try {
            const appUsersToDeleteRequests = usersToDelete.map((selected) => {
                const [appUserId, category] = selected.split('__');
                return httpDelete(`/appUsers/deleteAppUser/${appUserId}/${category}`);
            });
            await Promise.all(appUsersToDeleteRequests);
            setUsersToDelete([]);
            setSnackAlertState({
                message: 'API deleted successfully',
                icon: Icons.Delete as FC<SVGProps<SVGSVGElement>>
            });
            getAppUsers();
        } catch (err) {
            console.error('Failed to delete app users', err);
        }
    };

    const addNewAppUser = useCallback(async (appUser: AppUserProps) => {
        try {
            await httpPost('/appUsers/addAppUser', appUser);
            setShowCreateKeyPopup(false);
            setSnackAlertState({
                message: 'API key created successfully',
                icon: Icons.CircleCheck as FC<SVGProps<SVGSVGElement>>
            });
            getAppUsers();
        } catch (err) {
            console.error('Failed to delete app users', err);
        }
    }, []);

    const validateNewAppUser = (appUser: AppUserProps) => {
        const validations: { error: string; field: keyof AppUserProps }[] = [];
        if (!appUser.name) {
            validations.push({ error: 'API name is required', field: 'name' });
        } else if (appUsers.some((u) => u.name === appUser.name)) {
            validations.push({ error: 'API name already exists', field: 'name' });
        }
        if (appUser.category === undefined || appUser.category === null) {
            validations.push({ error: 'API type is required', field: 'category' });
        }
        if (!appUser.permissions) {
            validations.push({ error: 'Permissions are required', field: 'permissions' });
        }
        return validations;
    };

    const getTempAppUser = async () => {
        try {
            const { data } = await httpGet('/appUsers/getTempAppUserData');
            // @ts-ignore
            setTempAppUserData({ ...data, category: undefined, permissions: undefined, name: '' });
        } catch (err) {
            console.error('Failed to get temp auth user', err);
        }
    };

    const openCreateKeyPopup = () => {
        getTempAppUser();
        setShowCreateKeyPopup(true);
    };

    const updateFilter = useCallback((filter, value) => {
        setFilters((currentFilters) =>
            currentFilters.map((f) => {
                if (f.text === filter.text) {
                    f.selectedItems = value;
                }
                return f;
            })
        );
    }, []);

    const clearFilter = useCallback(
        (filter) => {
            const value = filter.multiple ? [] : undefined;
            updateFilter(filter, value);
        },
        [updateFilter]
    );

    const isFilterRemovable = useCallback(
        (filter) => (Array.isArray(filter.selectedItems) ? !!filter.selectedItems.length : !!filter.selectedItems),
        []
    );

    return (
        <div className="flex flex-col gap-4 h-full">
            <ExternalApi dispatch={dispatch} fields={fields} save={save} />
            <div className="flex justify-between">
                <div className="flex gap-2">
                    {filters.map((filter, i) => (
                        <Dropdown
                            variant={filter.variant}
                            multiple={filter.multiple}
                            searchable={filter.searchable}
                            key={filter.text}
                            title={filter.text}
                            items={filter.items}
                            removable={isFilterRemovable(filter)}
                            onRemove={() => clearFilter(filter)}
                            selectedItems={filter.selectedItems}
                            icon={filter.icon}
                            onChange={(selectedItems) => {
                                updateFilter(filter, selectedItems);
                            }}
                        />
                    ))}
                </div>
                <div className="flex gap-2">
                    <Search
                        className="w-[200px]"
                        placeholder={'Search API name'}
                        border
                        text={searchValue}
                        onChange={setSearchValue}
                    />
                    <Button
                        iconLeft={Icons.Add as FC<SVGProps<SVGSVGElement>>}
                        text={'Create Key'}
                        onClick={openCreateKeyPopup}
                    />
                </div>
            </div>
            <AuthUsersTable
                appUsers={appUsers}
                filters={filters}
                searchValue={searchValue}
                onCreate={openCreateKeyPopup}
                onDelete={setUsersToDelete}
                inactivityThreshold={inactivityThreshold}
            />
            {!!usersToDelete.length && (
                <DeleteAppUsersPopup
                    open={!!usersToDelete.length}
                    onClose={() => setUsersToDelete([])}
                    selections={usersToDelete.length}
                    onDelete={deleteAppUsers}
                />
            )}
            {showCreateKeyPopup && tempAppUserData && (
                <CreateKeyPopup
                    appUser={tempAppUserData}
                    open={showCreateKeyPopup}
                    createAppUser={addNewAppUser}
                    validateAppUser={validateNewAppUser}
                    onClose={() => setShowCreateKeyPopup(false)}
                />
            )}
            {snackAlertState && (
                <SnackAlert
                    anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
                    open
                    onClose={() => setSnackAlertState(undefined)}>
                    <SnackAlertContent iconLeft={snackAlertState.icon} message={snackAlertState.message} />
                </SnackAlert>
            )}
        </div>
    );
};

export default AuthTokensAndUsers;
